package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.ExecuteRosterMoveRequest
import com.sddamico.models.RosterMoveResponse
import com.sddamico.models.RosterPlayerResponse
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.selected

@Composable
fun LivActivateModal(
    livPlayer: RosterPlayerResponse,
    activeGolfers: List<RosterPlayerResponse>,
    openGolfSlots: List<String>,
    clubId: String,
    onClose: () -> Unit,
    onSuccess: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    var selectedSlot by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var warningMessage by remember { mutableStateOf<String?>(null) }

    Modal(onDismiss = onClose) {
        Column(Modifier.fillMaxWidth().gap(1.25.cssRem)) {
            // Header
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "Activate: ${livPlayer.name}",
                    Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.text)
                )
                Spacer()
                SportBadge(livPlayer.sport, livPlayer.isLiv)
            }

            // Description — copy depends on whether target slot is open or occupied
            val selectedIsOpen = selectedSlot.isNotEmpty() && selectedSlot in openGolfSlots
            val descriptionText = when {
                selectedSlot.isEmpty() -> "Choose where to put ${livPlayer.name}. Open slots don't require dropping a golfer."
                selectedIsOpen -> "${livPlayer.name} will be moved from the LIV Line into the open slot (no drop required)."
                else -> "${livPlayer.name} will be moved from the LIV Line, replacing the selected golfer."
            }
            SpanText(
                descriptionText,
                Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
            )

            if (activeGolfers.isEmpty() && openGolfSlots.isEmpty()) {
                SpanText(
                    "No golfer slots available",
                    Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem)
                )
            } else {
                // Target slot dropdown — open slots first, then occupied (swap) options
                Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                    SpanText(
                        "Target slot",
                        Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.textSecondary)
                    )
                    Select(attrs = {
                        addEventListener("change") { selectedSlot = it.target.asDynamic().value as? String ?: "" }
                        style {
                            width(100.percent)
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                        }
                    }) {
                        Option("", attrs = { if (selectedSlot.isEmpty()) selected() }) { Text("Select a target slot...") }
                        openGolfSlots.forEach { slot ->
                            Option(slot, attrs = { if (selectedSlot == slot) selected() }) {
                                Text("Open: ${formatSlotName(slot)}")
                            }
                        }
                        activeGolfers.forEach { golfer ->
                            Option(golfer.rosterSlot, attrs = { if (selectedSlot == golfer.rosterSlot) selected() }) {
                                Text("Replace ${golfer.name} (${formatSlotName(golfer.rosterSlot)})")
                            }
                        }
                    }
                }
            }

            // Warning message
            if (warningMessage != null) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#fefce8"))
                        .border(1.px, LineStyle.Solid, Color("#fde68a"))
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(warningMessage!!, Modifier.color(Color("#92400e")).fontSize(0.875.cssRem))
                }
            }

            // Error message
            if (errorMessage != null) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#fef2f2"))
                        .border(1.px, LineStyle.Solid, Color("#fca5a5"))
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(errorMessage!!, Modifier.color(Color("#991b1b")).fontSize(0.875.cssRem))
                }
            }

            // Buttons
            Row(Modifier.fillMaxWidth().gap(0.75.cssRem).position(Position.Relative), verticalAlignment = Alignment.CenterVertically) {
                Spacer()

                // Cancel button
                Div(
                    Modifier
                        .backgroundColor(SitePalette.border)
                        .color(SitePalette.text)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onClose) }
                        .toAttrs()
                ) {
                    Text("Cancel")
                }

                // Activate button
                val canSubmit = selectedSlot.isNotEmpty() && !submitting
                ConfirmableButton(
                    label = "Activate",
                    loadingLabel = "Activating...",
                    confirmLabel = "Are you sure?",
                    isLoading = submitting,
                    enabled = canSubmit,
                    buttonModifier = Modifier
                        .backgroundColor(if (canSubmit) SitePalette.error else SitePalette.border)
                        .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .transition(Transition.of("background-color", 150.ms)),
                    onConfirm = {
                        scope.launch {
                            submitting = true
                            errorMessage = null
                            try {
                                val body = apiJson.encodeToString(
                                    ExecuteRosterMoveRequest(
                                        moveType = "ROSTER_SWAP",
                                        clubId = clubId,
                                        rosterSlot = selectedSlot,
                                    )
                                )
                                val resp = apiPost("/api/rostermoves/execute", body, RosterMoveResponse.serializer())
                                if (resp.warning != null) {
                                    warningMessage = resp.warning
                                }
                                dismiss(onSuccess)
                            } catch (e: ApiException) {
                                errorMessage = e.errorMessage
                            } catch (_: Exception) {
                                errorMessage = "Failed to activate LIV player"
                            }
                            submitting = false
                        }
                    },
                )
            }
        }
    }
}

private fun formatSlotName(slot: String): String = when (slot) {
    "GOLFER_1" -> "Golfer 1"
    "GOLFER_2" -> "Golfer 2"
    "GOLFER_3" -> "Golfer 3"
    "GOLFER_4" -> "Golfer 4"
    "GOLFER_5" -> "Golfer 5"
    "LIV_LINE" -> "LIV Line"
    "MENS_TENNIS_1" -> "Men's Tennis 1"
    "MENS_TENNIS_2" -> "Men's Tennis 2"
    "MENS_TENNIS_3" -> "Men's Tennis 3"
    "MENS_TENNIS_4" -> "Men's Tennis 4"
    "WOMENS_TENNIS_1" -> "Women's Tennis 1"
    "WOMENS_TENNIS_2" -> "Women's Tennis 2"
    else -> slot
}
